import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { PageSEO } from '@/components/SEO';

// Layout
import { Navbar } from '@/components/layout/Navbar';

// Landing effects
import { CustomCursor } from '@/components/landing/effects/CustomCursor';
import { GrainOverlay } from '@/components/landing/effects/GrainOverlay';
import { ScrollProgress } from '@/components/landing/effects/ScrollProgress';

// Landing hooks
import { useLenis } from '@/components/landing/hooks/useLenis';

// Landing sections
import { HeroSection } from '@/components/landing/HeroSection';
import { ProblemSection } from '@/components/landing/ProblemSection';
import { PsychologyEngineSection } from '@/components/landing/PsychologyEngineSection';
import { BuiltForLogisticsSection } from '@/components/landing/BuiltForLogisticsSection';
import { HowItWorksSection } from '@/components/landing/HowItWorksSection';
import { FeatureShowcaseSection } from '@/components/landing/FeatureShowcaseSection';
import { FeaturesSection } from '@/components/landing/FeaturesSection';
import { SocialProofSection } from '@/components/landing/SocialProofSection';
import { PricingSection } from '@/components/landing/PricingSection';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { FooterSection } from '@/components/landing/FooterSection';

gsap.registerPlugin(ScrollTrigger);

interface LandingProps {
  skipAuthRedirect?: boolean;
}

export default function Landing({ skipAuthRedirect = false }: LandingProps) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();

  // Initialize Lenis smooth scroll (desktop only, respects reduced motion)
  useLenis();

  useEffect(() => {
    if (!skipAuthRedirect && isAuthenticated) {
      navigate('/tool');
    }
  }, [isAuthenticated, navigate, skipAuthRedirect]);

  return (
    <>
      <CustomCursor />
      <ScrollProgress />
      <GrainOverlay />

      <div className="min-h-screen bg-paper text-ink">
        <PageSEO.Landing />
        <Navbar
          showAuthButtons={!isAuthenticated || skipAuthRedirect}
          isHomePage={skipAuthRedirect && isAuthenticated}
          theme="light"
        />
        <main>
          <HeroSection />
          <ProblemSection />
          <PsychologyEngineSection />
          <BuiltForLogisticsSection />
          <HowItWorksSection />
          <FeatureShowcaseSection />
          <FeaturesSection />
          <SocialProofSection />
          <PricingSection />
          <FinalCTASection />
        </main>
        <FooterSection />
      </div>
    </>
  );
}
